import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../components/BrandLogo';
import { useAuth, type LoginForm } from '../auth/AuthContext';

interface LocationState {
  from?: { pathname?: string };
}

export default function SignInPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { currentUser, signIn, isAuthLoading, authError, isBootstrapping } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState<LoginForm>({ username: '', password: '' });

  if (isBootstrapping) {
    return (
      <main className="shell auth-shell" aria-busy="true">
        <p className="lede">Loading…</p>
      </main>
    );
  }

  if (currentUser) {
    const redirectTo = (location.state as LocationState | null)?.from?.pathname || '/';
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    try {
      await signIn(form);
      const redirectTo = (location.state as LocationState | null)?.from?.pathname || '/';
      navigate(redirectTo, { replace: true });
    } catch {
      // authError is already populated by AuthContext.
    }
  }

  return (
    <main className="shell auth-shell">
      <section className="hero">
        <div>
          <BrandLogo />
          <h1>{t('authTitle')}</h1>
          <p className="lede">{t('authDescription')}</p>
        </div>
        <div className="hero-panel language-panel">
          <label className="language-selector">
            <span>{t('appTitle')}</span>
            <select
              value={i18n.resolvedLanguage || i18n.language}
              onChange={(event) => i18n.changeLanguage(event.target.value)}
            >
              <option value="en">{t('english')}</option>
              <option value="am">{t('amharic')}</option>
              <option value="om">{t('oromo')}</option>
            </select>
          </label>
        </div>
      </section>

      <section className="card auth-card">
        <h2>{t('signIn')}</h2>
        <form onSubmit={handleSubmit}>
          <label>
            {t('username')}
            <input
              value={form.username}
              onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))}
            />
          </label>

          <label>
            {t('password')}
            <input
              type="password"
              value={form.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
            />
          </label>

          <button type="submit" disabled={isAuthLoading}>
            {isAuthLoading ? t('signingIn') : t('signIn')}
          </button>
          {authError ? <p className="error">{authError}</p> : null}
        </form>
      </section>
    </main>
  );
}
